import { useEffect, useState, useRef } from "react";
import { Plus, Edit, Trash2, X, Save, Upload, ImagePlus, GripVertical } from "lucide-react";
import { toast } from "sonner";
import {
  fetchGallery, adminCreateGallery, adminUpdateGallery, adminDeleteGallery,
  adminUploadImage, resolveImageUrl,
} from "@/lib/api";
import { invalidateGallery } from "@/hooks/useSiteContent";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const SPAN_OPTIONS = [
  { value: "md:col-span-4",                  label: "Small (4 cols)" },
  { value: "md:col-span-5",                  label: "Medium-Sm (5 cols)" },
  { value: "md:col-span-6",                  label: "Medium (6 cols)" },
  { value: "md:col-span-7",                  label: "Medium-Lg (7 cols)" },
  { value: "md:col-span-8",                  label: "Large (8 cols)" },
  { value: "md:col-span-8 md:row-span-2",    label: "Hero (8 cols × 2 rows)" },
  { value: "md:col-span-12",                 label: "Full Width (12 cols)" },
];

const EMPTY = { title: "", sub: "", image_url: "", span: "md:col-span-4", sort_order: 999 };

export const AdminGallery = ({ token }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const data = await fetchGallery();
      setItems(data.items || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openNew = () => {
    const nextOrder = (items.reduce((m, x) => Math.max(m, x.sort_order || 0), 0) || 0) + 1;
    setEditing({ ...EMPTY, sort_order: nextOrder });
  };

  const save = async () => {
    if (!editing.title.trim()) return toast.error("Title is required");
    if (!editing.image_url) return toast.error("Photo is required");
    setSaving(true);
    const payload = {
      title: editing.title.trim(),
      sub: editing.sub || "",
      image_url: editing.image_url,
      span: editing.span || "md:col-span-4",
      sort_order: Number(editing.sort_order) || 999,
    };
    try {
      if (editing.id) {
        await adminUpdateGallery(editing.id, payload, token);
        toast.success("Updated");
      } else {
        await adminCreateGallery(payload, token);
        toast.success("Added");
      }
      invalidateGallery();
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`Remove "${item.title}"?`)) return;
    try {
      await adminDeleteGallery(item.id, token);
      invalidateGallery();
      toast.success("Removed");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const data = await adminUploadImage(file, token);
      setEditing((c) => ({ ...c, image_url: data.url }));
      toast.success("Uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const sorted = [...items].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));

  return (
    <div data-testid="admin-gallery">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-heading text-3xl text-white">Gallery Photos</h2>
          <p className="text-white/50 text-sm uppercase tracking-widest mt-1">{items.length} tiles on the home gallery</p>
        </div>
        <button
          data-testid="gallery-add-btn"
          onClick={openNew}
          className="inline-flex items-center gap-2 bg-[#E60000] hover:bg-[#FF3333] text-white px-4 h-10 uppercase tracking-widest text-xs font-bold border-2 border-transparent hover:border-white"
        >
          <Plus className="w-4 h-4" /> Add Photo
        </button>
      </div>

      {loading ? (
        <div className="text-center py-20 text-white/50 uppercase tracking-widest">Loading…</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {sorted.map((it) => (
            <article key={it.id} data-testid={`gallery-card-${it.id}`} className="border-2 border-[#222] bg-[#1A1A1A] p-3 flex gap-3 hover:border-[#E60000] transition-colors">
              <div className="w-20 h-20 bg-black border border-[#333] overflow-hidden flex-shrink-0">
                {it.image_url ? <img src={resolveImageUrl(it.image_url)} alt={it.title} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-white/30"><ImagePlus className="w-6 h-6" /></div>}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-widest text-white/40 font-bold mb-0.5 flex items-center gap-1">
                  <GripVertical className="w-3 h-3" /> #{it.sort_order}
                </div>
                <h4 className="font-heading text-base text-white truncate leading-tight">{it.title}</h4>
                <p className="text-white/55 text-xs line-clamp-2">{it.sub}</p>
              </div>
              <div className="flex flex-col gap-1">
                <button onClick={() => setEditing({ ...it })} data-testid={`gallery-edit-${it.id}`} className="w-8 h-8 flex items-center justify-center bg-[#0A0A0A] border border-[#333] hover:border-[#E60000] hover:text-[#E60000] text-white/70"><Edit className="w-4 h-4" /></button>
                <button onClick={() => remove(it)} data-testid={`gallery-delete-${it.id}`} className="w-8 h-8 flex items-center justify-center bg-[#0A0A0A] border border-[#333] hover:bg-[#E60000] hover:border-[#E60000] text-white/70 hover:text-white"><Trash2 className="w-4 h-4" /></button>
              </div>
            </article>
          ))}
        </div>
      )}

      {editing && (
        <div data-testid="gallery-editor" className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div className="bg-[#141414] border-2 border-[#E60000] max-w-xl w-full max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b-2 border-[#333] bg-black flex items-center justify-between">
              <h3 className="font-heading text-2xl text-white">{editing.id ? "Edit Photo" : "Add Photo"}</h3>
              <button onClick={() => setEditing(null)} className="w-9 h-9 flex items-center justify-center text-white/70 hover:text-white hover:bg-[#E60000]"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="text-[10px] uppercase tracking-widest text-white/70 font-bold mb-2 block">Photo</label>
                <div className="flex gap-4">
                  <div className="w-28 h-28 bg-black border-2 border-[#333] overflow-hidden flex-shrink-0">
                    {editing.image_url ? <img src={resolveImageUrl(editing.image_url)} alt="preview" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-white/30"><ImagePlus className="w-8 h-8" /></div>}
                  </div>
                  <div className="flex-1 space-y-2">
                    <input ref={fileRef} type="file" accept="image/*" onChange={upload} className="hidden" />
                    <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} className="inline-flex items-center gap-2 bg-[#0A0A0A] border-2 border-[#333] hover:border-[#E60000] text-white px-3 h-10 uppercase tracking-widest text-[10px] font-bold disabled:opacity-50">
                      <Upload className="w-4 h-4" /> {uploading ? "Uploading…" : "Upload Photo"}
                    </button>
                    <Input value={editing.image_url || ""} onChange={(e) => setEditing((c) => ({ ...c, image_url: e.target.value }))} placeholder="Or paste an image URL" className="bg-[#0A0A0A] border-2 border-[#333] rounded-none h-10 text-white text-sm" />
                  </div>
                </div>
              </div>

              <div>
                <label className="text-[10px] uppercase tracking-widest text-white/70 font-bold mb-2 block">Title</label>
                <Input value={editing.title} onChange={(e) => setEditing((c) => ({ ...c, title: e.target.value }))} className="bg-[#0A0A0A] border-2 border-[#333] rounded-none h-11 text-white" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-white/70 font-bold mb-2 block">Subtitle / Caption</label>
                <Textarea rows={2} value={editing.sub} onChange={(e) => setEditing((c) => ({ ...c, sub: e.target.value }))} className="bg-[#0A0A0A] border-2 border-[#333] rounded-none text-white text-sm" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-white/70 font-bold mb-2 block">Tile size</label>
                  <Select value={editing.span} onValueChange={(v) => setEditing((c) => ({ ...c, span: v }))}>
                    <SelectTrigger className="bg-[#0A0A0A] border-2 border-[#333] rounded-none h-11 text-white uppercase tracking-wider text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#1A1A1A] border-2 border-[#333] text-white rounded-none">
                      {SPAN_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value} className="text-xs">{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-white/70 font-bold mb-2 block">Sort order</label>
                  <Input type="number" value={editing.sort_order} onChange={(e) => setEditing((c) => ({ ...c, sort_order: e.target.value }))} className="bg-[#0A0A0A] border-2 border-[#333] rounded-none h-11 text-white" />
                </div>
              </div>
            </div>
            <div className="px-6 py-4 border-t-2 border-[#333] bg-black flex justify-end gap-2">
              <button onClick={() => setEditing(null)} className="bg-transparent border-2 border-[#333] hover:border-[#E60000] text-white/70 hover:text-white px-4 h-10 uppercase tracking-widest text-xs font-bold">Cancel</button>
              <button onClick={save} disabled={saving} data-testid="gallery-save" className="inline-flex items-center gap-2 bg-[#E60000] hover:bg-[#FF3333] text-white px-5 h-10 uppercase tracking-widest text-xs font-bold border-2 border-transparent hover:border-white disabled:opacity-50">
                <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
